import { FC, useState } from 'react';
import { Col, InputNumber, Select, Space, Typography, Button, Modal, Form } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { SettingsCard, FormRow, FormLabel } from '../EU6Calculator.styles';
import { CalculatorSettings as ICalculatorSettings, Currency, FXRates } from '../EU6Calculator.types';
import { CURRENCY_OPTIONS } from '@/constants/currency';
import { useGetTradingDealsQuery } from '@/api';

const { Text } = Typography;

interface CalculatorSettingsProps {
  settings: ICalculatorSettings;
  fxRates: FXRates;
  onUpdate: (key: keyof ICalculatorSettings, value: any) => void;
  onUpdateFXRates: (rates: Partial<FXRates>) => void;
}

export const CalculatorSettings: FC<CalculatorSettingsProps> = ({
  settings,
  fxRates,
  onUpdate,
  onUpdateFXRates
}) => {
  const { data: tradingDealsData, isLoading: areTradingDealsLoading } = useGetTradingDealsQuery();
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [form] = Form.useForm();

  const handleTradingDealChange = (tradingDealId: string) => {
    const selectedDeal = tradingDealsData?.tradingDeals.find((deal) => deal._id === tradingDealId);
    if (selectedDeal) {
      onUpdate('tradingDealId', tradingDealId);
      onUpdate('tradingDealName', selectedDeal.name);
      onUpdate('tradingDealPercentage', selectedDeal.percentage);
    }
  };

  const handleEditRates = () => {
    form.setFieldsValue({
      EUR: fxRates[Currency.EUR],
      USD: fxRates[Currency.USD],
      CAD: fxRates[Currency.CAD]
    });
    setIsEditingRates(true);
  };

  const handleSaveRates = async () => {
    try {
      const values = await form.validateFields();
      onUpdateFXRates({
        [Currency.EUR]: values.EUR,
        [Currency.USD]: values.USD,
        [Currency.CAD]: values.CAD
      });
      setIsEditingRates(false);
    } catch (error) {
      // Validation failed
    }
  };

  return (
    <>
      <Modal
        title="Edit FX Rates"
        open={isEditingRates}
        onOk={handleSaveRates}
        onCancel={() => setIsEditingRates(false)}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="1 EUR (in GBP)"
            name="EUR"
            rules={[
              { required: true, message: 'Please enter EUR rate' },
              { type: 'number', min: 0, message: 'Rate must be positive' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={4}
              placeholder="Enter EUR to GBP rate"
            />
          </Form.Item>
          <Form.Item
            label="1 USD (in GBP)"
            name="USD"
            rules={[
              { required: true, message: 'Please enter USD rate' },
              { type: 'number', min: 0, message: 'Rate must be positive' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={4}
              placeholder="Enter USD to GBP rate"
            />
          </Form.Item>
          <Form.Item
            label="1 CAD (in GBP)"
            name="CAD"
            rules={[
              { required: true, message: 'Please enter CAD rate' },
              { type: 'number', min: 0, message: 'Rate must be positive' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={4}
              placeholder="Enter CAD to GBP rate"
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Note: Base currency is GBP (1.0000). All conversions are relative to GBP.
          </Text>
        </Form>
      </Modal>
      <SettingsCard
        title="Calculator Settings"
        extra={
          <Space split={<Text type="secondary">|</Text>}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              International Rates: 1 EUR = {fxRates[Currency.EUR].toFixed(2)} GBP
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              1 USD = {fxRates[Currency.USD].toFixed(2)} GBP
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              1 CAD = {fxRates[Currency.CAD].toFixed(2)} GBP
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              1 GBP = {(fxRates[Currency.GBP] / fxRates[Currency.USD]).toFixed(2)} USD
            </Text>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={handleEditRates}
              style={{ marginLeft: 8 }}
            >
              Edit
            </Button>
          </Space>
        }
      >
        <FormRow gutter={[16, 16]} align="top">
          <Col span={6}>
            <FormLabel>Total Budget (Plan Total)</FormLabel>
            <InputNumber
              style={{ width: '100%' }}
              value={settings.totalBudget}
              onChange={(value) => onUpdate('totalBudget', value || 0)}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
              min={0}
              size="large"
              placeholder="Enter total budget"
            />
          </Col>

          <Col span={6}>
            <FormLabel>Sell CPM</FormLabel>
            <InputNumber
              style={{ width: '100%' }}
              value={settings.sellCPM}
              onChange={(value) => onUpdate('sellCPM', value || 0)}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
              min={0}
              step={0.01}
              size="large"
              placeholder="Enter sell CPM"
            />
          </Col>

          <Col span={6}>
            <FormLabel>Display/Plan Currency</FormLabel>
            <Select
              style={{ width: '100%' }}
              value={settings.displayCurrency}
              onChange={(value) => onUpdate('displayCurrency', value)}
              size="large"
              options={CURRENCY_OPTIONS}
            />
          </Col>

          <Col span={6}>
            <FormLabel>Hard Costs Total</FormLabel>
            <InputNumber
              style={{ width: '100%' }}
              value={settings.hardCostsTotal}
              onChange={(value) => onUpdate('hardCostsTotal', value || 0)}
              min={0}
              size="large"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
            />
          </Col>
        </FormRow>

        <FormRow gutter={[16, 16]} align="top">
          <Col span={12}>
            <FormLabel>Buying Point</FormLabel>
            <Select
              style={{ width: '100%' }}
              value={settings.tradingDealId}
              onChange={handleTradingDealChange}
              size="large"
              loading={areTradingDealsLoading}
              placeholder="Select buying point"
              options={
                tradingDealsData?.tradingDeals
                  .filter((deal) => deal.isActive)
                  .map((deal) => ({
                    label: deal.name,
                    value: deal._id
                  })) || []
              }
            />
          </Col>
        </FormRow>
      </SettingsCard>
    </>
  );
};
